import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { DatePicker, Input, Select } from 'antd';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import GetField from '../Components/GetField';
import styles from '../styles';
import { getGrandTotal } from './common-functions';

const AntSelect = GetField(Select);
const AntInput = GetField(Input);
const { Option } = Select;
const AntDatePicker = GetField(DatePicker);

const FabricForm = props => {
  const {
    classes,
    getValues,
    setValue,
    control,
    watch,
    fabricItem,
    fabricIndex,
    fabricAppend,
    fabricRemove,
  } = props;

  const fabricsForJobchart = useSelector(state => state.fabricsForJobchart);
  const colorIds = useSelector(state => state.colorIds);

  const [widths, setWidths] = useState([]);

  useEffect(() => {
    if (fabricsForJobchart.fabricNames.length) {
      const fabricName = getValues(`fabrics[${fabricIndex}].fabric`);

      if (fabricsForJobchart.fabricIndexes[fabricName] !== undefined) {
        setWidths(fabricsForJobchart.widths[fabricName]);
      } else {
        setWidths([]);
      }
    }
  }, [fabricsForJobchart, watch(`fabrics[${fabricIndex}].fabric`)]);

  useEffect(() => {
    const quantity = getValues(`fabrics[${fabricIndex}].quantity`) || 0;
    const rate = getValues(`fabrics[${fabricIndex}].rate`) || 0;
    const gst = getValues(`fabrics[${fabricIndex}].gst`) || 0;

    const value = +(+quantity * +rate).toFixed(2) || 0;
    const gstValue = +((+value * +gst) / 100).toFixed(2);
    const total = +(+value + +gstValue || 0).toFixed(2);
    const singleTotal = +(total / (quantity || 1)).toFixed(2);

    setValue(`fabrics[${fabricIndex}].value`, value);
    setValue(`fabrics[${fabricIndex}].gstValue`, gstValue);
    setValue(`fabrics[${fabricIndex}].singleTotal`, singleTotal);
    setValue(`fabrics[${fabricIndex}].total`, total);
  }, [
    watch(`fabrics[${fabricIndex}].quantity`),
    watch(`fabrics[${fabricIndex}].rate`),
    watch(`fabrics[${fabricIndex}].gst`),
  ]);

  useEffect(() => {
    const fabricItems = getValues('fabrics');
    const accessoryItems = getValues('accessories');
    const items = getValues('items');
    const stylesItems = getValues('styles');
    setValue(
      'grandTotal',
      getGrandTotal(fabricItems, accessoryItems, items, stylesItems)
    );
  }, [watch(`fabrics[${fabricIndex}].total`)]);

  return (
    <tr>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].fabric`}
          control={control}
          defaultValue={fabricItem.fabric}
          render={({ field }) => (
            <AntSelect placeholder="Select fabric" {...field}>
              {fabricsForJobchart.fabricNames.map(fabric => (
                <Option key={fabric} value={fabric}>
                  {fabric}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].fabricId`}
          control={control}
          defaultValue={fabricItem.fabricId}
          render={({ field }) => (
            <AntSelect placeholder="Select width" {...field}>
              {widths.map(width => (
                <Option key={width.id} value={width.id}>
                  {width.width}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].baseColorId`}
          control={control}
          defaultValue={fabricItem.baseColorId}
          render={({ field }) => (
            <AntSelect placeholder="Select color" {...field}>
              {colorIds.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].printColorId`}
          control={control}
          defaultValue={fabricItem.printColorId}
          render={({ field }) => (
            <AntSelect placeholder="Select color" {...field}>
              {colorIds.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].quantity`}
          control={control}
          defaultValue={fabricItem.quantity}
          render={({ field }) => (
            <AntInput
              placeholder="Quantity"
              {...field}
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].rate`}
          control={control}
          defaultValue={fabricItem.rate}
          render={({ field }) => (
            <AntInput
              placeholder="Price"
              {...field}
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].value`}
          control={control}
          defaultValue={fabricItem.value}
          render={({ field }) => (
            <AntInput
              placeholder="Amount"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].gst`}
          control={control}
          defaultValue={fabricItem.gst}
          render={({ field }) => (
            <AntInput
              placeholder="GST (%)"
              {...field}
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].gstValue`}
          control={control}
          defaultValue={fabricItem.gstValue}
          render={({ field }) => (
            <AntInput
              placeholder="GST Amount"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].singleTotal`}
          control={control}
          defaultValue={fabricItem.singleTotal}
          render={({ field }) => (
            <AntInput
              placeholder="Total"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].total`}
          control={control}
          defaultValue={fabricItem.total}
          render={({ field }) => (
            <AntInput
              placeholder="Total"
              {...field}
              type="number"
              min={0}
              size="small"
              disabled
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].hsn`}
          control={control}
          defaultValue={fabricItem.hsn}
          render={({ field }) => <AntInput placeholder="HSN Code" {...field} />}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].deliveryDate`}
          control={control}
          render={({ field }) => (
            <AntDatePicker allowClear placeholder="Date" {...field} />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <DeleteOutlined
          onClick={() => {
            fabricRemove(fabricIndex);
          }}
        />
        &nbsp; &nbsp;
        <PlusCircleOutlined onClick={() => fabricAppend({})} />
      </td>
    </tr>
  );
};

FabricForm.propTypes = {
  classes: PropTypes.object.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  control: PropTypes.any.isRequired,
  watch: PropTypes.func.isRequired,
  fabricItem: PropTypes.object.isRequired,
  fabricIndex: PropTypes.number.isRequired,
  fabricAppend: PropTypes.func.isRequired,
  fabricRemove: PropTypes.func.isRequired,
};

export default withStyles(styles)(FabricForm);
